
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface ExpenseReportDialogProps {
  organizationId: string;
  expenses: any[];
  onReportCreated: () => void;
}

// Helper function to ensure consistent date handling
const formatDateForStorage = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

const ExpenseReportDialog = ({ 
  organizationId, 
  expenses,
  onReportCreated 
}: ExpenseReportDialogProps) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const totalAmount = expenses.reduce((sum, expense) => sum + Number(expense.amount), 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Get min and max dates from selected expenses
      const minDate = new Date(Math.min(...expenses.map(e => new Date(e.date).getTime())));
      const maxDate = new Date(Math.max(...expenses.map(e => new Date(e.date).getTime())));
      
      // Format dates consistently using our helper function
      const formattedMinDate = formatDateForStorage(minDate);
      const formattedMaxDate = formatDateForStorage(maxDate);

      const { error } = await supabase
        .from('expense_reports')
        .insert({
          organization_id: organizationId,
          generated_by: user.id,
          total_amount: totalAmount,
          date_from: formattedMinDate,
          date_to: formattedMaxDate,
          report_type: 'expense',
          format: 'pdf',
          status: 'pending',
          currency: expenses[0]?.currency || 'USD'
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Expense report submitted successfully",
      });
      
      setOpen(false);
      onReportCreated();
    } catch (error: any) {
      console.error('Submit error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to submit expense report",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <FileText className="h-4 w-4" />
          Create Expense Report
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create Expense Report</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Total Amount</Label>
            <Input
              type="text"
              value={`$${totalAmount.toFixed(2)}`}
              disabled
            />
          </div>
          <div className="space-y-2">
            <Label>Number of Expenses</Label>
            <Input
              type="text"
              value={expenses.length}
              disabled
            />
          </div>
          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Report"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ExpenseReportDialog;
